import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { reviewsAPI, employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Star, User, Loader2, Save } from 'lucide-react';

const ReviewNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [selectedRecordId, setSelectedRecordId] = useState(searchParams.get('record_id') || '');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(searchParams.get('employee_id') || '');
  
  const [formData, setFormData] = useState({
    review_type: searchParams.get('cadence') || 'one-time',
    performance_rating: 0,
    key_achievements: '',
    areas_to_improve: '',
    goals_next_period: '',
    manager_notes: '',
    share_manager_notes: false
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await employerAPI.getRecords('acknowledged');
      const acknowledgedRecords = res.data.filter(r => r.status === 'acknowledged' && r.employee_id);
      setRecords(acknowledgedRecords);
      
      // Pre-select employee if URL param provided
      if (searchParams.get('record_id')) {
        const record = acknowledgedRecords.find(r => r.id === searchParams.get('record_id'));
        if (record) {
          setSelectedEmployeeId(record.employee_id);
        }
      }
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Failed to load employee records');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordSelect = (recordId) => {
    setSelectedRecordId(recordId);
    const record = records.find(r => r.id === recordId);
    if (record) {
      setSelectedEmployeeId(record.employee_id);
    }
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, performance_rating: rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedRecordId || !selectedEmployeeId) {
      toast.error('Please select an employee');
      return;
    }
    
    if (formData.performance_rating === 0) {
      toast.error('Please provide a performance rating');
      return;
    }
    
    setSubmitting(true);
    try {
      await reviewsAPI.create({
        record_id: selectedRecordId,
        employee_id: selectedEmployeeId,
        review_type: formData.review_type,
        performance_rating: formData.performance_rating,
        key_achievements: formData.key_achievements,
        areas_to_improve: formData.areas_to_improve,
        goals_next_period: formData.goals_next_period,
        manager_notes: formData.manager_notes,
        share_manager_notes: formData.share_manager_notes
      });
      
      toast.success('Review submitted successfully');
      navigate('/employer/reviews');
    } catch (error) {
      console.error('Error creating review:', error);
      toast.error(error.response?.data?.detail || 'Failed to create review');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRecord = records.find(r => r.id === selectedRecordId);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (records.length === 0) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#5B6472] hover:text-[#0A0F1E]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
            <User className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No employees to review</h3>
            <p className="text-[#5B6472]">
              You can only create reviews for employees who have acknowledged their VETT Record.
            </p>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="review-new">
        {/* Header */}
        <div className="animate-fade-in">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#5B6472] hover:text-[#0A0F1E] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">New Performance Review</h1>
          <p className="mt-2 text-[#5B6472]">Evaluate employee performance and set goals</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Employee Selection */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Select Employee</h2>
            <Select value={selectedRecordId} onValueChange={handleRecordSelect}>
              <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                <SelectValue placeholder="Choose an employee" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                {records.map(record => (
                  <SelectItem key={record.id} value={record.id} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                    <div className="flex items-center gap-2">
                      <span>
                        {record.employee_first_name || record.employee_name || record.employee_email}
                        {record.employee_last_name && ` ${record.employee_last_name}`}
                      </span>
                      <span className="text-[#5B6472]">- {record.job_title}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedRecord && (
              <div className="mt-4 p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                    {selectedRecord.employee_photo ? (
                      <img src={selectedRecord.employee_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-[#5B6472]" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#0A0F1E]">
                      {selectedRecord.employee_first_name || selectedRecord.employee_name || selectedRecord.employee_email}
                      {selectedRecord.employee_last_name && ` ${selectedRecord.employee_last_name}`}
                    </p>
                    <p className="text-[#5B6472] text-sm">{selectedRecord.job_title}</p>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Review Type */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Review Type</h2>
            <Select value={formData.review_type} onValueChange={(v) => setFormData(prev => ({ ...prev, review_type: v }))}>
              <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                <SelectItem value="one-time" className="text-[#0A0F1E] hover:bg-[#F5F8FF]">One-Time Review</SelectItem>
                <SelectItem value="weekly" className="text-[#0A0F1E] hover:bg-[#F5F8FF]">Weekly (Recurring)</SelectItem>
                <SelectItem value="monthly" className="text-[#0A0F1E] hover:bg-[#F5F8FF]">Monthly (Recurring)</SelectItem>
                <SelectItem value="quarterly" className="text-[#0A0F1E] hover:bg-[#F5F8FF]">Quarterly (Recurring)</SelectItem>
              </SelectContent>
            </Select>
            {formData.review_type !== 'one-time' && (
              <p className="text-[#5B6472] text-sm mt-2">
                Next review will be automatically scheduled after this one.
              </p>
            )}
          </Card>

          {/* Performance Rating */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Performance Rating *</h2>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => handleRatingClick(rating)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`rating-star-${rating}`}
                >
                  <Star 
                    className={`w-10 h-10 ${
                      rating <= formData.performance_rating 
                        ? 'fill-[#1B5AFF] text-[#1B5AFF]' 
                        : 'text-[#5B6472] hover:text-[#1B5AFF]/50'
                    }`} 
                  />
                </button>
              ))}
            </div>
            <p className="text-[#5B6472] text-sm mt-2">
              {formData.performance_rating === 0 && 'Click to rate'}
              {formData.performance_rating === 1 && 'Needs significant improvement'}
              {formData.performance_rating === 2 && 'Below expectations'}
              {formData.performance_rating === 3 && 'Meets expectations'}
              {formData.performance_rating === 4 && 'Exceeds expectations'}
              {formData.performance_rating === 5 && 'Exceptional performance'}
            </p>
          </Card>

          {/* Review Content */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 space-y-6 animate-fade-in">
            <div>
              <Label className="text-[#0A0F1E]">Key Achievements</Label>
              <Textarea
                value={formData.key_achievements}
                onChange={(e) => setFormData(prev => ({ ...prev, key_achievements: e.target.value }))}
                placeholder="What did this employee accomplish well?"
                className="mt-2 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
              />
            </div>
            
            <div>
              <Label className="text-[#0A0F1E]">Areas to Improve</Label>
              <Textarea
                value={formData.areas_to_improve}
                onChange={(e) => setFormData(prev => ({ ...prev, areas_to_improve: e.target.value }))}
                placeholder="What areas need development?"
                className="mt-2 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
              />
            </div>
            
            <div>
              <Label className="text-[#0A0F1E]">Goals for Next Period</Label>
              <Textarea
                value={formData.goals_next_period}
                onChange={(e) => setFormData(prev => ({ ...prev, goals_next_period: e.target.value }))}
                placeholder="What should the employee focus on next?"
                className="mt-2 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
              />
            </div>
          </Card>

          {/* Manager Notes */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Manager Notes</h2>
                <p className="text-[#5B6472] text-sm">Internal notes, not shared by default</p>
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="share_notes" className="text-[#0A0F1E] text-sm">Share with employee</Label>
                <Switch
                  id="share_notes"
                  checked={formData.share_manager_notes}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, share_manager_notes: checked }))}
                />
              </div>
            </div>
            <Textarea
              value={formData.manager_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, manager_notes: e.target.value }))}
              placeholder="Add private notes about this review..."
              className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
            />
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !selectedRecordId || formData.performance_rating === 0}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              data-testid="submit-review-btn"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Submit Review
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default ReviewNew;
